import { z } from "zod";
import type { infer as zInfer } from "zod";
import { currency } from "./currency";

export const timesAllowedToCompleteFilterKeys = [
  "eventIds",
  "marketIds",
  "marketTypeNames",
  "sports",
  "meetingIds",
  "seasonIds",
  "tournamentIds",
] as const;

export type TimesAllowedToCompleteFilterKey =
  (typeof timesAllowedToCompleteFilterKeys)[number];

export const automationExcludedEventTypes = [
  "SEASON",
  "TOURNAMENT",
  "MATCH",
  "RACE",
] as const;

/*
    Automation is what runs both tokens and triggers on backend.
    They are very different things on the frontend side of things but share the same data shape.
    This model holds all possible variations and token and automation models then pick and choose what they need.
 */

export const userDrivenAttributes = z.object({
  // Deposit
  minimumAmount: z.number().optional(),
  provider: z
    .enum([
      "SKRILL",
      "MERCHANT_WARRIOR",
      "MERCHANT_WARRIOR_PAY_ID",
      "MERCHANT_WARRIOR_APPLE_PAY",
      "MERCHANT_WARRIOR_GOOGLE_PAY",
      "MANUAL",
      "WITHDRAWAL_BOUNCE_BACK",
      "PAYMENT_IQ",
      "POLI",
    ])
    .optional(),
  currency: currency.optional(),
  numOfDeposits: z.number().optional(),
  cumulativeAmount: z.number().optional(),
  firstDeposit: z.boolean().optional(),

  // Non SEM Bet Stake
  minimumSelections: z.number().optional(),
  maximumSelections: z.number().optional(),
  minimumSelectionOdds: z.number().optional(),

  // SEM Bet Stake
  minimumOutcomes: z.number().optional(),
  maximumOutcomes: z.number().optional(),
  minimumOutcomeOdds: z.number().optional(),

  // Shared
  minimumExpectedResults: z
    .array(
      z.object({
        minimum: z.number(),
        resultType: z.string(),
      }),
    )
    .optional(),
  numOfBetsRequired: z.number().optional(),
  minimumStake: z.number().optional(),
  maximumStake: z.number().optional(),
  marketIds: z.array(z.string()).optional(),
  allMarketIdsMustMatch: z.boolean().optional(),
  marketTypeNames: z.array(z.string()).optional(),
  allMarketTypeNamesMustMatch: z.boolean().optional(),
  eventIds: z.array(z.string()).optional(),
  allEventIdsMustMatch: z.boolean().optional(),
  meetingIds: z.array(z.string()).optional(),
  allMeetingIdsMustMatch: z.boolean().optional(),
  seasonIds: z.array(z.string()).optional(),
  allSeasonIdsMustMatch: z.boolean().optional(),
  tournamentIds: z.array(z.string()).optional(),
  allTournamentIdsMustMatch: z.boolean().optional(),
  competitorIds: z.array(z.string()).optional(),
  allCompetitorIdsMustMatch: z.boolean().optional(),
  outcomeGroups: z.array(z.string()).optional(),
  allOutcomeGroupsMustMatch: z.boolean().optional(),
  stakeSource: z.enum(["AVAILABLE", "PROMOTION_USER"]).optional(),
  minimumOdds: z.number().optional(),
  currencies: z.array(currency).optional(),
  sports: z.array(z.string()).optional(),
  allSportsMustMatch: z.boolean().optional(),
  timesAllowedToCompletePerEvent: z.number().optional(),
  timesAllowedToCompletePerEventFilter: z
    .array(z.enum(timesAllowedToCompleteFilterKeys))
    .optional(),
  excludedMarketIds: z.array(z.string()).optional(),
  excludedMarketTypeNames: z.array(z.string()).optional(),
  excludedEventIds: z.array(z.string()).optional(),
  excludedMeetingIds: z.array(z.string()).optional(),
  excludedSeasonIds: z.array(z.string()).optional(),
  excludedTournamentIds: z.array(z.string()).optional(),
  excludedSports: z.array(z.string()).optional(),
  excludedEventTypes: z.array(z.enum(automationExcludedEventTypes)).optional(),
  excludePromotionalMarkets: z.boolean().optional(),
  excludeLiveMarkets: z.boolean().optional(),
});

export const automationComponent = z.object({
  type: z.enum([
    // actions
    "GIVE_CASH",
    "ODDS_BOOST",
    "GIVE_PROMOTION",
    "DEPOSIT_MATCH",
    "STAKE_REFUND",
    "WINNINGS_BOOST",
    "GIVE_TOKEN",
    "GIVE_CAMPAIGN",
    "TAG_USER",
    "ADD_WITHDRAWAL_TO_BATCH",
    "PAY_AS_WINNER",
    // requirements
    "DEPOSIT",
    "NON_SEM_BET_STAKE",
    "SEM_BET_STAKE",
    "COMBO_MULTI_BET_STAKE",
    "QUADDIE_BET_STAKE",
    "NON_SEM_BET_SETTLE",
    "NON_SEM_BET_FINALISE",
    "COMBO_MULTI_BET_SETTLE",
    "COMBO_MULTI_BET_FINALISE",
    "QUADDIE_BET_SETTLE",
    "QUADDIE_BET_FINALISE",
    "SEM_BET_SETTLE",
    "BET_VOID",
    "BET_ROLLBACK",
    "WITHDRAW",
    "BETTING_EVENT_RESULTED",
    "CODE_REDEMPTION",
    "SIGNUP",
    "CONDITIONAL_RESULT",
  ]),
  info: z.string(),
  projectedRewardInfo: z
    .object({
      max: z.number().nullable().optional(),
      percent: z.number().nullable().optional(),
      isPromo: z.boolean().nullable().optional(),
    })
    .optional()
    .nullable(),
  status: z.string().optional().nullable(),
  attributeInfos: z.record(z.string(), z.string()).optional().nullable(),
  userDrivenAttributes: userDrivenAttributes.optional().nullable(),
  isCumulative: z.boolean().optional(),
});

export const baseAutomation = z.object({
  name: z.string(),
  automationId: z.string(),
  campaign: z.object({
    bettingTabs: z.array(
      z.enum([
        "SPORTS_SINGLES",
        "SPORTS_SEM",
        "RACING_SINGLES",
        "RACING_SEM",
        "RACING_EXOTICS",
        "RACING_TRIFECTA",
        "RACING_FIRST_4",
        "RACING_EXACTA",
        "RACING_QUINELLA",
      ]),
    ),
    termsOfUse: z.string().nullish(),
    name: z.string(),
    openToAllEligibleUsers: z.boolean(),
    description: z.string().nullish(),
    provider: z.enum(["INTERNAL", "BRAZE"]),
    status: z.enum(["ACTIVE", "COMPLETED"]),
  }),
  requirements: z.array(automationComponent).nullish(),
  actions: z.array(automationComponent).nullish(),
  requirementInfos: z.array(z.string()).optional(),
});

export type AutomationAction = zInfer<typeof automationComponent>;
export type BaseAutomation = zInfer<typeof baseAutomation>;
